import { useState, useEffect } from 'react';
import {
  Rocket,
  Server,
  AlertTriangle,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  GitCommit,
  Globe,
  ExternalLink,
  Zap,
  X,
  RotateCcw,
  RefreshCw,
  Power,
  User,
} from 'lucide-react';
import type {
  Deployment,
  Service,
  Incident,
  UptimeStatus,
  OperationsSummary,
} from '../types/operations';
import './Operations.css';

type TabType = 'deployments' | 'services' | 'incidents' | 'uptime';

// Default empty state
const defaultSummary: OperationsSummary = {
  activeDeployments: 0,
  failedDeployments: 0,
  servicesHealthy: 0,
  servicesUnhealthy: 0,
  openIncidents: 0,
  criticalIncidents: 0,
  uptimeOverall: 0,
};

export default function Operations() {
  const [activeTab, setActiveTab] = useState<TabType>('deployments');
  const [summary, setSummary] = useState<OperationsSummary>(defaultSummary);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [uptime, setUptime] = useState<UptimeStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDeployment, setSelectedDeployment] = useState<Deployment | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [actionPending, setActionPending] = useState(false);

  useEffect(() => {
    const fetchOperationsData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [summaryRes, deploymentsRes, servicesRes, incidentsRes, uptimeRes] = await Promise.all([
          fetch('/api/operations/summary'),
          fetch('/api/operations/deployments'),
          fetch('/api/operations/services'),
          fetch('/api/operations/incidents'),
          fetch('/api/operations/uptime'),
        ]);

        if (summaryRes.ok) {
          const data = await summaryRes.json();
          setSummary(data);
        }
        if (deploymentsRes.ok) {
          const data = await deploymentsRes.json();
          if (data?.deployments) setDeployments(data.deployments);
        }
        if (servicesRes.ok) {
          const data = await servicesRes.json();
          if (data?.services) setServices(data.services);
        }
        if (incidentsRes.ok) {
          const data = await incidentsRes.json();
          if (data?.incidents) setIncidents(data.incidents);
        }
        if (uptimeRes.ok) {
          const data = await uptimeRes.json();
          if (data?.monitors) setUptime(data.monitors);
        }
      } catch (err) {
        console.error('Failed to fetch operations data:', err);
        setError('Failed to load operations data');
      } finally {
        setLoading(false);
      }
    };

    fetchOperationsData();
  }, []);

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const handleRollback = async (deploymentId: string) => {
    if (!confirm('Are you sure you want to rollback this deployment? This will create an approval request.')) return;
    setActionPending(true);
    try {
      // In production, this would POST to an API to create an approval request
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Rollback request submitted for approval. Check the Approvals page.');
      setSelectedDeployment(null);
    } catch (err) {
      console.error('Failed to request rollback:', err);
    } finally {
      setActionPending(false);
    }
  };

  const handleServiceRestart = async (serviceId: string) => {
    if (!confirm('Are you sure you want to restart this service? This will create an approval request.')) return;
    setActionPending(true);
    try {
      // In production, this would POST to an API to create an approval request
      await new Promise(resolve => setTimeout(resolve, 1000));
      alert('Service restart request submitted for approval. Check the Approvals page.');
      setSelectedService(null);
    } catch (err) {
      console.error('Failed to request restart:', err);
    } finally {
      setActionPending(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="ops-loading">Loading operations data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Operations</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#f38ba8' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Operations</h1>
        <p>Deployments, services, and infrastructure health</p>
      </div>

      {/* Stats */}
      <div className="ops-stats">
        <div className="ops-stat">
          <div className="ops-stat-icon success">
            <Rocket size={24} />
          </div>
          <div className="ops-stat-content">
            <div className="ops-stat-value">{summary.activeDeployments}</div>
            <div className="ops-stat-label">Active Deploys</div>
          </div>
        </div>
        <div className="ops-stat">
          <div className="ops-stat-icon success">
            <Server size={24} />
          </div>
          <div className="ops-stat-content">
            <div className="ops-stat-value">{summary.servicesHealthy}</div>
            <div className="ops-stat-label">Healthy Services</div>
          </div>
        </div>
        <div className="ops-stat">
          <div className="ops-stat-icon warning">
            <AlertTriangle size={24} />
          </div>
          <div className="ops-stat-content">
            <div className="ops-stat-value">{summary.openIncidents}</div>
            <div className="ops-stat-label">Open Incidents</div>
          </div>
        </div>
        <div className="ops-stat">
          <div className="ops-stat-icon info">
            <Activity size={24} />
          </div>
          <div className="ops-stat-content">
            <div className="ops-stat-value">{summary.uptimeOverall}%</div>
            <div className="ops-stat-label">Overall Uptime</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="ops-tabs">
        <button
          className={`ops-tab ${activeTab === 'deployments' ? 'active' : ''}`}
          onClick={() => setActiveTab('deployments')}
        >
          <Rocket size={16} />
          Deployments
          <span className="count">{deployments.length}</span>
        </button>
        <button
          className={`ops-tab ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          <Server size={16} />
          Services
          <span className="count">{services.length}</span>
        </button>
        <button
          className={`ops-tab ${activeTab === 'incidents' ? 'active' : ''}`}
          onClick={() => setActiveTab('incidents')}
        >
          <AlertTriangle size={16} />
          Incidents
          <span className="count">{incidents.length}</span>
        </button>
        <button
          className={`ops-tab ${activeTab === 'uptime' ? 'active' : ''}`}
          onClick={() => setActiveTab('uptime')}
        >
          <Activity size={16} />
          Uptime
          <span className="count">{uptime.length}</span>
        </button>
      </div>

      {/* Deployments */}
      {activeTab === 'deployments' && (
        <div className="deployments-list">
          {deployments.length === 0 ? (
            <div className="ops-empty">
              <div className="ops-empty-icon">
                <Rocket size={48} />
              </div>
              <h3>No recent deployments</h3>
              <p>Deployments will appear here when triggered.</p>
            </div>
          ) : (
            deployments.map((deploy) => (
              <div
                key={deploy.id}
                className="deployment-item clickable"
                onClick={() => setSelectedDeployment(deploy)}
              >
                <div className="deployment-header">
                  <div className="deployment-title">
                    <span className={`provider-badge ${deploy.provider}`}>
                      {deploy.provider}
                    </span>
                    <span className="deployment-project">{deploy.projectName}</span>
                    <span className={`deployment-env ${deploy.environment}`}>
                      {deploy.environment}
                    </span>
                  </div>
                  <span className={`status-badge ${deploy.status}`}>
                    <span className="status-dot" />
                    {deploy.status}
                  </span>
                </div>
                <div className="deployment-meta">
                  {deploy.branch && (
                    <span>
                      <GitCommit size={12} />
                      {deploy.branch}
                    </span>
                  )}
                  {deploy.commit && (
                    <span className="deployment-commit">
                      {deploy.commit}
                    </span>
                  )}
                  <span>
                    <Clock size={12} />
                    {formatTimeAgo(deploy.createdAt)}
                  </span>
                  {deploy.duration && (
                    <span>
                      <Zap size={12} />
                      {formatDuration(deploy.duration)}
                    </span>
                  )}
                  {deploy.url && (
                    <a href={deploy.url} target="_blank" rel="noopener noreferrer" style={{ color: '#89b4fa', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Globe size={12} />
                      Visit
                      <ExternalLink size={10} />
                    </a>
                  )}
                </div>
                {deploy.commitMessage && (
                  <div className="deployment-message">
                    "{deploy.commitMessage}"
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Services */}
      {activeTab === 'services' && (
        <div className="services-grid">
          {services.map((service) => (
            <div
              key={service.id}
              className="service-card clickable"
              onClick={() => setSelectedService(service)}
            >
              <div className="service-header">
                <div>
                  <span className={`provider-badge ${service.provider}`}>
                    {service.provider}
                  </span>
                  <span className="service-name" style={{ marginLeft: '0.5rem' }}>
                    {service.name}
                  </span>
                </div>
                <span className={`status-badge ${service.status}`}>
                  <span className="status-dot" />
                  {service.status}
                </span>
              </div>
              <div className="service-details">
                {service.url && (
                  <div className="service-detail">
                    <Globe size={14} />
                    <a href={service.url} target="_blank" rel="noopener noreferrer" className="service-url">
                      {service.url.replace('https://', '')}
                    </a>
                  </div>
                )}
                {service.region && (
                  <div className="service-detail">
                    <Server size={14} />
                    {service.region}
                  </div>
                )}
                {service.resources && (
                  <div className="service-detail">
                    <Zap size={14} />
                    {service.resources.cpu} / {service.resources.memory}
                  </div>
                )}
                {service.healthCheck && (
                  <div className="service-detail">
                    {service.healthCheck.status === 'healthy' ? (
                      <CheckCircle size={14} style={{ color: '#a6e3a1' }} />
                    ) : (
                      <XCircle size={14} style={{ color: '#f38ba8' }} />
                    )}
                    Health: {service.healthCheck.status}
                  </div>
                )}
                {service.lastDeployedAt && (
                  <div className="service-detail">
                    <Clock size={14} />
                    Last deploy: {formatTimeAgo(service.lastDeployedAt)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Incidents */}
      {activeTab === 'incidents' && (
        <div className="incidents-list">
          {incidents.length === 0 ? (
            <div className="ops-empty">
              <div className="ops-empty-icon">
                <CheckCircle size={48} />
              </div>
              <h3>No open incidents</h3>
              <p>All systems operational.</p>
            </div>
          ) : (
            incidents.map((incident) => (
              <div key={incident.id} className={`incident-item ${incident.severity}`}>
                <div className="incident-header">
                  <div className="incident-title">{incident.title}</div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <span className={`status-badge ${incident.status}`}>
                      {incident.status.replace('_', ' ')}
                    </span>
                    <span className={`severity-badge ${incident.severity}`}>
                      {incident.severity}
                    </span>
                  </div>
                </div>
                <div className="incident-description">{incident.description}</div>
                <div className="incident-meta">
                  <span>
                    <Clock size={12} />
                    Opened {formatTimeAgo(incident.createdAt)}
                  </span>
                  <span>Source: {incident.source}</span>
                  {incident.assignee && <span>Assignee: {incident.assignee}</span>}
                </div>
                {incident.affectedSystems.length > 0 && (
                  <div className="incident-systems">
                    {incident.affectedSystems.map((system) => (
                      <span key={system} className="incident-system-tag">
                        {system}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Uptime */}
      {activeTab === 'uptime' && (
        <div className="uptime-grid">
          {uptime.map((monitor) => (
            <div key={monitor.id} className="uptime-card">
              <div className="uptime-header">
                <span className="uptime-name">{monitor.name}</span>
                <span className={`status-badge ${monitor.status}`}>
                  <span className="status-dot" />
                  {monitor.status}
                </span>
              </div>
              <a
                href={monitor.url}
                target="_blank"
                rel="noopener noreferrer"
                className="service-url"
                style={{ fontSize: '0.8rem' }}
              >
                {monitor.url.replace('https://', '')}
              </a>
              <div className="uptime-details">
                <div className="uptime-stat">
                  <div className={`uptime-stat-value ${monitor.responseTime && monitor.responseTime > 500 ? 'warning' : 'good'}`}>
                    {monitor.responseTime}ms
                  </div>
                  <div className="uptime-stat-label">Response</div>
                </div>
                <div className="uptime-stat">
                  <div className={`uptime-stat-value ${monitor.uptime30d && monitor.uptime30d >= 99.9 ? 'good' : monitor.uptime30d && monitor.uptime30d >= 99 ? 'warning' : 'bad'}`}>
                    {monitor.uptime30d}%
                  </div>
                  <div className="uptime-stat-label">30d Uptime</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Deployment Detail Modal */}
      {selectedDeployment && (
        <div className="modal-overlay" onClick={() => setSelectedDeployment(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-row">
                <h2>Deployment Details</h2>
                <span className={`status-badge ${selectedDeployment.status}`}>
                  <span className="status-dot" />
                  {selectedDeployment.status}
                </span>
              </div>
              <button className="close-btn" onClick={() => setSelectedDeployment(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="deployment-detail-header">
                <span className={`provider-badge ${selectedDeployment.provider}`}>
                  {selectedDeployment.provider}
                </span>
                <span className="deployment-project">{selectedDeployment.projectName}</span>
                <span className={`deployment-env ${selectedDeployment.environment}`}>
                  {selectedDeployment.environment}
                </span>
              </div>

              <div className="detail-grid">
                {selectedDeployment.branch && (
                  <div className="detail-item">
                    <span className="detail-label">Branch</span>
                    <span><GitCommit size={14} style={{ marginRight: '6px' }} />{selectedDeployment.branch}</span>
                  </div>
                )}
                {selectedDeployment.commit && (
                  <div className="detail-item">
                    <span className="detail-label">Commit</span>
                    <span className="mono">{selectedDeployment.commit}</span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Created</span>
                  <span>{new Date(selectedDeployment.createdAt).toLocaleString()}</span>
                </div>
                {selectedDeployment.duration && (
                  <div className="detail-item">
                    <span className="detail-label">Duration</span>
                    <span><Zap size={14} style={{ marginRight: '6px' }} />{formatDuration(selectedDeployment.duration)}</span>
                  </div>
                )}
                {selectedDeployment.triggeredBy && (
                  <div className="detail-item">
                    <span className="detail-label">Triggered By</span>
                    <span><User size={14} style={{ marginRight: '6px' }} />{selectedDeployment.triggeredBy}</span>
                  </div>
                )}
              </div>

              {selectedDeployment.commitMessage && (
                <div className="detail-section">
                  <h3>Commit Message</h3>
                  <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                    "{selectedDeployment.commitMessage}"
                  </p>
                </div>
              )}

              <div className="modal-actions">
                {selectedDeployment.url && (
                  <a
                    href={selectedDeployment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <Globe size={16} />
                    Visit Deployment
                    <ExternalLink size={14} />
                  </a>
                )}
                {selectedDeployment.status === 'ready' && (
                  <button
                    className="btn btn-danger"
                    onClick={() => handleRollback(selectedDeployment.id)}
                    disabled={actionPending}
                  >
                    <RotateCcw size={16} />
                    {actionPending ? 'Submitting...' : 'Request Rollback'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Detail Modal */}
      {selectedService && (
        <div className="modal-overlay" onClick={() => setSelectedService(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-row">
                <h2>{selectedService.name}</h2>
                <span className={`status-badge ${selectedService.status}`}>
                  <span className="status-dot" />
                  {selectedService.status}
                </span>
              </div>
              <button className="close-btn" onClick={() => setSelectedService(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px' }}>
                <span className={`provider-badge ${selectedService.provider}`}>
                  {selectedService.provider}
                </span>
              </div>

              <div className="detail-grid">
                {selectedService.url && (
                  <div className="detail-item">
                    <span className="detail-label">URL</span>
                    <a href={selectedService.url} target="_blank" rel="noopener noreferrer" className="service-url">
                      {selectedService.url.replace('https://', '')}
                    </a>
                  </div>
                )}
                {selectedService.region && (
                  <div className="detail-item">
                    <span className="detail-label">Region</span>
                    <span>{selectedService.region}</span>
                  </div>
                )}
                {selectedService.resources && (
                  <>
                    <div className="detail-item">
                      <span className="detail-label">CPU</span>
                      <span>{selectedService.resources.cpu}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Memory</span>
                      <span>{selectedService.resources.memory}</span>
                    </div>
                  </>
                )}
                {selectedService.healthCheck && (
                  <div className="detail-item">
                    <span className="detail-label">Health Check</span>
                    <span style={{ color: selectedService.healthCheck.status === 'healthy' ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                      {selectedService.healthCheck.status === 'healthy' ? <CheckCircle size={14} style={{ marginRight: '6px' }} /> : <XCircle size={14} style={{ marginRight: '6px' }} />}
                      {selectedService.healthCheck.status}
                    </span>
                  </div>
                )}
                {selectedService.lastDeployedAt && (
                  <div className="detail-item">
                    <span className="detail-label">Last Deployed</span>
                    <span>{formatTimeAgo(selectedService.lastDeployedAt)}</span>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                {selectedService.url && (
                  <a
                    href={selectedService.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <Globe size={16} />
                    Open Service
                    <ExternalLink size={14} />
                  </a>
                )}
                <button
                  className="btn btn-warning"
                  onClick={() => handleServiceRestart(selectedService.id)}
                  disabled={actionPending}
                >
                  <RefreshCw size={16} />
                  {actionPending ? 'Submitting...' : 'Request Restart'}
                </button>
                <button
                  className="btn btn-danger"
                  disabled={true}
                >
                  <Power size={16} />
                  Stop Service
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
